import { observer } from 'mobx-react-lite';
import { regionsStore } from '../stores';
import React from 'react';
import { reaction } from 'mobx';

/**
 * Основной хук для работы с RegionsStore
 * @returns {RegionsStore} Экземпляр store
 */
export const useRegions = () => {
  return regionsStore;
};

/**
 * Хук для получения данных регионов с автоматической загрузкой
 * @param {boolean} autoFetch - Автоматически загружать данные при монтировании
 * @returns {Object} Объект с данными регионов и состоянием загрузки
 */
export const useRegionsData = (autoFetch = true) => {
  const store = useRegions();
  
  React.useEffect(() => {
    if (autoFetch && store.shouldFetch) {
      store.fetchRegions().catch(console.error);
    }
  }, [store, autoFetch]);

  return {
    regions: store.regions,
    allGroups: store.allGroups,
    regionsList: store.regionsList,
    isLoading: store.isLoading,
    error: store.error,
    hasData: store.hasData,
    stats: store.getStats()
  };
};

/**
 * Хук для получения конкретной группы по ID
 * @param {string|number} groupId - ID группы
 * @returns {Object|null} Объект группы или null
 */
export const useGroupById = (groupId) => {
  const store = useRegions();
  
  React.useEffect(() => {
    if (store.shouldFetch) {
      store.fetchRegions().catch(console.error);
    }
  }, [store]);

  return store.getGroupById(groupId);
};

/**
 * Хук для поиска групп
 * @param {string} searchTerm - Поисковый запрос
 * @returns {Array} Массив найденных групп
 */
export const useGroupsSearch = (searchTerm) => {
  const store = useRegions();
  
  React.useEffect(() => {
    if (store.shouldFetch) {
      store.fetchRegions().catch(console.error);
    }
  }, [store]);

  return store.searchGroups(searchTerm);
};

/**
 * Хук для получения групп по региону
 * @param {string} regionName - Название региона
 * @returns {Array} Массив групп региона
 */
export const useGroupsByRegion = (regionName) => {
  const store = useRegions();
  
  React.useEffect(() => {
    if (store.shouldFetch) {
      store.fetchRegions().catch(console.error);
    }
  }, [store]);

  return store.getGroupsByRegion(regionName);
};

/**
 * Хук для получения групп по городу
 * @param {string} cityName - Название города
 * @returns {Array} Массив групп города
 */
export const useGroupsByCity = (cityName) => {
  const store = useRegions();
  
  React.useEffect(() => {
    if (store.shouldFetch) {
      store.fetchRegions().catch(console.error);
    }
  }, [store]);

  return store.getGroupsByCity(cityName);
};

/**
 * Хук для подписки на изменения данных регионов
 * @param {Function} callback - Функция обратного вызова
 * @param {Array} dependencies - Зависимости для useEffect
 */
export const useRegionsSubscription = (callback, dependencies = []) => {
  const store = useRegions();
  
  React.useEffect(() => {
    const disposer = reaction(
      () => ({
        regions: store.regions,
        hasData: store.hasData,
        lastFetchTime: store.lastFetchTime
      }),
      (data) => {
        callback(data);
      },
      { fireImmediately: true }
    );
    
    return disposer;
  }, [store, callback, ...dependencies]);
};

/**
 * Хук для управления загрузкой данных
 * @returns {Object} Методы для управления загрузкой
 */
export const useRegionsActions = () => {
  const store = useRegions();
  
  return {
    fetchRegions: (forceRefresh = false) => store.fetchRegions(forceRefresh),
    refreshRegions: () => store.refreshRegions(),
    clearCache: () => store.clearCache(),
    reset: () => store.reset()
  };
};

/**
 * HOC для оборачивания компонентов в observer
 * @param {React.Component} Component - Компонент для оборачивания
 * @returns {React.Component} Обернутый компонент
 */
export const withRegions = (Component) => {
  return observer(Component);
};
